import type { Reducer, ReducerState, ReducerAction, RefObject, Dispatch, SetStateAction } from 'react'
import { useRef, useEffect, useReducer, useState, useCallback } from 'react'

/** Allows you to check if a component is still mounted */
export const useMountedRef = (): RefObject<boolean> => {
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )
  return mountedRef
}

/**
 * Analog for `useState`, but only updates if the component is still mounted.
 *
 * 🔹 Behaves just like `useState`, except:
 * - The returned setter will no-op if the component is unmounted.
 *
 * This avoids warnings or memory leaks when async logic tries to update state
 * after the component has been removed from the tree.
 */
export function useUnmountableState<T>(initialState: T): [T, Dispatch<SetStateAction<T>>] {
  const mountedRef = useMountedRef()
  const [state, unsafeSetState] = useState(initialState)

  const setState = useCallback(
    (dispatch: SetStateAction<T>) => {
      if (mountedRef.current) unsafeSetState(dispatch)
    },
    [mountedRef]
  )

  return [state, setState]
}

// --- Overload signature (mirror React’s useReducer) ---
export function useUnmountableReducer<R extends Reducer<unknown, unknown>>(
  reducer: R,
  initialState: ReducerState<R>
): [ReducerState<R>, (action: ReducerAction<R>) => void]

// --- Overload signature (mirror React’s useReducer) ---
export function useUnmountableReducer<R extends Reducer<unknown, unknown>, I>(
  reducer: R,
  initializerArg: I,
  initializer: (arg: I) => ReducerState<R>
): [ReducerState<R>, (action: ReducerAction<R>) => void]

/**
 * Analog for `useReducer`, but only dispatches if the component is still mounted.
 *
 * 🔹 Behaves just like `useReducer`, except:
 * - The returned `dispatch` function will no-op if the component is unmounted.
 *
 * Mirrors both overloads of React’s `useReducer`:
 * ```ts
 * const [state, dispatch] = useUnmountableReducer(reducer, initialState)
 * const [state, dispatch] = useUnmountableReducer(reducer, initArg, initFn)
 * ```
 */
export function useUnmountableReducer<R extends Reducer<unknown, unknown>, I>(
  reducer: R,
  initializerArg: unknown,
  initializer?: (arg: I) => ReducerState<R>
): [ReducerState<R>, (action: ReducerAction<R>) => void] {
  const mountedRef = useMountedRef()
  const [state, unsafeDispatch] = useReducer(reducer, initializerArg, initializer)

  const dispatch = useCallback(
    (action: ReducerAction<R>) => {
      if (mountedRef.current) unsafeDispatch(action)
    },
    [mountedRef]
  )

  return [state, dispatch]
}
